import * as React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Link,
    Tabs,
    Tab,
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import AppTheme from '../theme/AppTheme';
import CssBaseline from '@mui/material/CssBaseline';
import Messages from './Messages';
import EditContent from './EditContent';
import { API_BASE, CARD_SX } from './dashboardShared';
import { COLORS } from '../theme/colors';

const TOKEN_KEY = 'alphatnc_dashboard_token';

// Dark-mode categorical slots (fixed order — do not cycle) from the site's validated palette.
const BLUE = '#3987e5';
const ORANGE = '#d95926';
const AQUA = '#199e70';
const YELLOW = '#c98500';
const MAGENTA = '#d55181';
const SOURCE_COLORS = [BLUE, ORANGE, AQUA, YELLOW, MAGENTA];
const DEVICE_COLORS = [BLUE, ORANGE, AQUA];

const TOOLTIP_STYLE = {
    contentStyle: { background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8 },
    labelStyle: { color: '#c3c2b7' },
    itemStyle: { color: '#ffffff' },
};

function formatDuration(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
}

function KpiTile({ label, value }) {
    return (
        <Card sx={{ ...CARD_SX, flex: 1, minWidth: 160 }}>
            <CardContent>
                <Typography variant="body2" sx={{ color: 'grey.400' }}>{label}</Typography>
                <Typography variant="h4" sx={{ color: 'white', mt: 0.5 }}>{value}</Typography>
            </CardContent>
        </Card>
    );
}

function LoginForm({ onLogin }) {
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/dashboard/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || 'Login failed');
                return;
            }
            sessionStorage.setItem(TOKEN_KEY, data.token);
            onLogin(data.token);
        } catch (err) {
            setError('Could not reach the server. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <Card sx={{ width: 360, ...CARD_SX }}>
                <CardContent>
                    <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>Owner Dashboard</Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                        <Stack spacing={2}>
                            {error && <Alert severity="error">{error}</Alert>}
                            <TextField
                                id="dashboard-password"
                                type="password"
                                label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                fullWidth
                                slotProps={{ inputLabel: { sx: { color: 'white' } } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: COLORS.overlay,
                                        color: 'white',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                }}
                            />
                            <Button type="submit" variant="contained" disabled={loading} fullWidth>
                                {loading ? 'Checking…' : 'Log in'}
                            </Button>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

function AnalyticsPanel({ token, onLogout }) {
    const [metrics, setMetrics] = React.useState(null);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/dashboard/metrics`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (cancelled) return;
                if (res.status === 401) {
                    onLogout();
                    return;
                }
                if (!res.ok) {
                    setError(data.message || 'Failed to load analytics data');
                    return;
                }
                setMetrics(data);
            } catch (err) {
                if (!cancelled) setError('Could not reach the server. Please try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, onLogout]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2}>
                <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" sx={{ color: 'grey.400', mb: 3 }}>Last 30 days</Typography>

            {metrics.sample && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Showing sample data for preview — connect Google Analytics to see real traffic
                    and conversion numbers.
                </Alert>
            )}

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={3}>
                <KpiTile label="Active users" value={metrics.summary.activeUsers.toLocaleString()} />
                <KpiTile label="Sessions" value={metrics.summary.sessions.toLocaleString()} />
                <KpiTile label="Avg. session length" value={formatDuration(metrics.summary.avgSessionDurationSeconds)} />
                <KpiTile label="Contact form submissions" value={metrics.summary.formSubmissions.toLocaleString()} />
            </Stack>

            <Stack spacing={3}>
                <Card sx={CARD_SX}>
                    <CardContent>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Daily active users</Typography>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={metrics.dailyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="#2c2c2a" vertical={false} />
                                <XAxis dataKey="date" stroke="#898781" tick={{ fontSize: 12 }} />
                                <YAxis stroke="#898781" tick={{ fontSize: 12 }} allowDecimals={false} />
                                <Tooltip {...TOOLTIP_STYLE} />
                                <Line type="monotone" dataKey="users" stroke={BLUE} strokeWidth={2} dot={false} name="Active users" />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <Card sx={{ ...CARD_SX, flex: 1 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Sessions by traffic source</Typography>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={metrics.sources} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke="#2c2c2a" vertical={false} />
                                    <XAxis dataKey="name" stroke="#898781" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="#898781" tick={{ fontSize: 12 }} allowDecimals={false} />
                                    <Tooltip {...TOOLTIP_STYLE} />
                                    <Bar dataKey="value" name="Sessions" radius={[4, 4, 0, 0]}>
                                        {metrics.sources.map((_, i) => (
                                            <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    <Card sx={{ ...CARD_SX, flex: 1 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Sessions by device</Typography>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={metrics.devices} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke="#2c2c2a" vertical={false} />
                                    <XAxis dataKey="name" stroke="#898781" tick={{ fontSize: 12 }} />
                                    <YAxis stroke="#898781" tick={{ fontSize: 12 }} allowDecimals={false} />
                                    <Tooltip {...TOOLTIP_STYLE} />
                                    <Bar dataKey="value" name="Sessions" radius={[4, 4, 0, 0]}>
                                        {metrics.devices.map((_, i) => (
                                            <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Stack>

                <Card sx={CARD_SX}>
                    <CardContent>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Top countries</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: 'grey.400' }}>Country</TableCell>
                                    <TableCell sx={{ color: 'grey.400' }} align="right">Sessions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {metrics.topCountries.map((row) => (
                                    <TableRow key={row.name}>
                                        <TableCell sx={{ color: 'white' }}>{row.name}</TableCell>
                                        <TableCell sx={{ color: 'white' }} align="right">{row.value.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );
}

const TABS = ['Analytics', 'Messages', 'Edit Website'];

function DashboardContent({ token, onLogout }) {
    const [tab, setTab] = React.useState(0);

    return (
        <Box sx={{ padding: { xs: 2, md: 4 }, minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h4" sx={{ color: 'white' }}>Website Dashboard</Typography>
                <Button size="small" onClick={onLogout} sx={{ color: 'grey.400' }}>Log out</Button>
            </Stack>

            <Tabs
                value={tab}
                onChange={(e, newTab) => setTab(newTab)}
                textColor="inherit"
                indicatorColor="primary"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'rgba(255,255,255,0.15)' }}
            >
                {TABS.map((label) => (
                    <Tab key={label} label={label} sx={{ color: 'grey.400' }} />
                ))}
            </Tabs>

            {tab === 0 && <AnalyticsPanel token={token} onLogout={onLogout} />}
            {tab === 1 && <Messages token={token} onLogout={onLogout} />}
            {tab === 2 && <EditContent token={token} onLogout={onLogout} />}

            <Link href="/" sx={{ color: COLORS.link, mt: 3, display: 'inline-block' }}>&larr; Back to site</Link>
        </Box>
    );
}

export default function Dashboard() {
    const [token, setToken] = React.useState(() => sessionStorage.getItem(TOKEN_KEY));

    const handleLogout = React.useCallback(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
    }, []);

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <Box sx={{ bgcolor: '#0a0a0a', minHeight: '100vh' }}>
                {token ? (
                    <DashboardContent token={token} onLogout={handleLogout} />
                ) : (
                    <LoginForm onLogin={setToken} />
                )}
            </Box>
        </AppTheme>
    );
}
